import React from 'react'
import PropTypes from 'prop-types'
import Chart from './Chart'

function FormattedContent({ content }) {
  if (!content) return null

  const formatInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g)
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
      if (linkMatch) {
        const label = linkMatch[1]
        const url = linkMatch[2]
        // Only render real in-app relative paths as links. Reject bare "/",
        // and any second character that a browser would normalize into a
        // protocol-relative authority ("//host" or "/\host" -> "//host").
        if (url.startsWith('/') && url.length > 1 && url[1] !== '/' && url[1] !== '\\') {
          return <a key={index} className="ai-record-link" href={url}>{label}</a>
        }
        return label
      }
      return part
    })
  }

  const isSeparatorLine = (line) => {
    const trimmed = line.trim()
    return /^\|[-:\s|]+\|$/.test(trimmed) && trimmed.includes('---')
  }

  const renderTable = (lines, key) => {
    const rows = lines
      .filter(line => line.trim() && !isSeparatorLine(line))
      .map(line => {
        const cells = line.split('|')
        if (cells[0].trim() === '') cells.shift()
        if (cells.length > 0 && cells[cells.length - 1].trim() === '') cells.pop()
        return cells.map(cell => cell.trim())
      })
      .filter(row => row.length > 0)

    if (rows.length === 0) return null

    const headers = rows[0]
    const dataRows = rows.slice(1)

    return (
      <div key={`table-${key}`} className="ai-table-wrapper">
        <table className="ai-table">
          <thead>
            <tr>
              {headers.map((header, i) => (
                <th key={i}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{formatInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const parseChartBlock = (content) => {
    try {
      const parsed = JSON.parse(content)
      // Validate it looks like a chart config
      if (parsed && parsed.type && parsed.data && Array.isArray(parsed.data)) {
        return parsed
      }
      return null
    } catch (e) {
      return null
    }
  }

  // Check for raw JSON chart objects in content (AI sometimes outputs without backticks)
  const renderWithInlineCharts = (text, keyPrefix) => {
    const results = []
    let remaining = text
    let idx = 0

    // Look for JSON objects that start with {"type"
    while (remaining.length > 0) {
      const jsonStart = remaining.indexOf('{"type"')
      if (jsonStart === -1) {
        if (remaining.trim()) {
          results.push(<span key={`${keyPrefix}-text-${idx}`}>{formatInline(remaining)}</span>)
        }
        break
      }

      // Add text before the JSON
      if (jsonStart > 0) {
        const before = remaining.slice(0, jsonStart)
        if (before.trim()) {
          results.push(<span key={`${keyPrefix}-text-${idx}`}>{formatInline(before)}</span>)
          idx++
        }
      }

      // Try to find matching closing brace by counting braces
      let braceCount = 0
      let jsonEnd = -1
      for (let i = jsonStart; i < remaining.length; i++) {
        if (remaining[i] === '{') braceCount++
        if (remaining[i] === '}') braceCount--
        if (braceCount === 0) {
          jsonEnd = i + 1
          break
        }
      }

      if (jsonEnd > jsonStart) {
        const jsonStr = remaining.slice(jsonStart, jsonEnd)
        const config = parseChartBlock(jsonStr)
        if (config) {
          results.push(<Chart key={`${keyPrefix}-chart-${idx}`} config={config} />)
        } else {
          results.push(<span key={`${keyPrefix}-json-${idx}`}>{jsonStr}</span>)
        }
        remaining = remaining.slice(jsonEnd)
      } else {
        // Couldn't find matching brace, output rest as text
        results.push(<span key={`${keyPrefix}-text-${idx}`}>{formatInline(remaining)}</span>)
        break
      }
      idx++
    }

    return results
  }

  const parts = content.split(/(```[\s\S]*?```)/g)

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('```')) {
          const langMatch = part.match(/^```(\w*)\n?/)
          const language = langMatch ? langMatch[1] : ''

          if (language === 'chart') {
            const chartContent = part.replace(/```chart\n?/, '').replace(/```$/, '').trim()
            const config = parseChartBlock(chartContent)
            if (config) {
              return <Chart key={index} config={config} />
            }
            return (
              <div key={index} className="ai-chart-container">
                <p style={{ color: '#ef4444' }}>Error: Invalid chart JSON</p>
                <pre style={{ fontSize: '12px', marginTop: '8px' }}>{chartContent}</pre>
              </div>
            )
          }

          const code = part.replace(/```\w*\n?/, '').replace(/```$/, '')
          return (
            <pre key={index} className="ai-code-block">
              <code>{code}</code>
            </pre>
          )
        }

        if (part.includes('|')) {
          const lines = part.split('\n')
          const tableLines = []
          let inTable = false
          const otherContent = []

          lines.forEach((line, lineIndex) => {
            if (line.trim().startsWith('|')) {
              inTable = true
              tableLines.push(line)
            } else {
              if (inTable && tableLines.length > 0) {
                otherContent.push(renderTable(tableLines, lineIndex))
                tableLines.length = 0
              }
              inTable = false
              if (line.trim()) {
                otherContent.push(<p key={`p-${lineIndex}`}>{formatInline(line)}</p>)
              }
            }
          })

          if (tableLines.length > 0) {
            otherContent.push(renderTable(tableLines, 'end'))
          }

          return <div key={index}>{otherContent}</div>
        }

        return (
          <div key={index}>
            {part.split('\n\n').map((paragraph, pIndex) => {
              if (!paragraph.trim()) return null
              // Check if paragraph contains a chart JSON
              if (paragraph.includes('"type"') && paragraph.includes('"data"')) {
                return <p key={pIndex}>{renderWithInlineCharts(paragraph, `p-${index}-${pIndex}`)}</p>
              }
              return <p key={pIndex}>{formatInline(paragraph)}</p>
            })}
          </div>
        )
      })}
    </>
  )
}

FormattedContent.propTypes = {
  content: PropTypes.string
}

export default FormattedContent
